import React, { useEffect, useRef, useState } from "react";
import { X, Play, ArrowCounterClockwise, CheckCircle } from "@phosphor-icons/react";
import api from "../../lib/api";
import { toast } from "sonner";

/**
 * Reproductor fullscreen del juego "Luces de colores".
 *
 * Reproduce la secuencia configurada. Si hay `target_color`, el atleta
 * tiene que tocar la pantalla cuando aparezca → medimos reaction time.
 * Al terminar, guarda el resultado en /neuroscience/results.
 */
export default function ColorLightsPlayer({ config, onClose }) {
  const [phase, setPhase] = useState("intro"); // intro | playing | done
  const [stepIdx, setStepIdx] = useState(-1);
  const [repeatIdx, setRepeatIdx] = useState(0);
  const [currentColor, setCurrentColor] = useState("#0A0A0A");
  const [taps, setTaps] = useState(0);
  const [correctTaps, setCorrectTaps] = useState(0);
  const [reactionTimes, setReactionTimes] = useState([]);
  const startedAtRef = useRef(null);
  const targetShownAtRef = useRef(null);
  const tappedThisStepRef = useRef(false);
  const cancelledRef = useRef(false);

  const sequence = config.sequence || [];
  const repeats = config.repeats || 1;
  const restMs = config.rest_ms_between_repeats || 0;
  const target = (config.target_color || "").toLowerCase();
  // Default true: legacy configs (sin el campo) también se randomizan, salvo que el admin lo desactive.
  const randomize = config.randomize !== false;
  const targetProb = typeof config.target_probability === "number" ? config.target_probability : 0.3;

  // Fisher-Yates shuffle (no muta el array original)
  const shuffled = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // Construye una ronda con el % de target deseado. Si no hay target o no es random,
  // devuelve simplemente la secuencia barajada (o la original si !randomize).
  const buildRound = () => {
    if (!randomize) return sequence;
    if (!target) return shuffled(sequence);

    const targetSteps = sequence.filter((s) => (s.color || "").toLowerCase() === target);
    const nonTargetSteps = sequence.filter((s) => (s.color || "").toLowerCase() !== target);

    // Si el target color no está en la paleta de la secuencia, lo creamos como template.
    const targetTemplate = targetSteps[0] || {
      color: config.target_color,
      duration_ms: nonTargetSteps[0]?.duration_ms || 1000,
      label: "Target",
    };
    // Si todos los steps son target o ninguno es non-target, no hay con qué rellenar → fallback a shuffle plano.
    if (!nonTargetSteps.length) return shuffled(sequence);

    const total = sequence.length;
    const targetCount = Math.max(0, Math.min(total, Math.round(total * targetProb)));
    const out = [];
    for (let i = 0; i < targetCount; i++) out.push(targetTemplate);
    for (let i = 0; i < total - targetCount; i++) {
      out.push(nonTargetSteps[Math.floor(Math.random() * nonTargetSteps.length)]);
    }
    return shuffled(out);
  };

  // Reproducción de la secuencia
  useEffect(() => {
    if (phase !== "playing") return;
    cancelledRef.current = false;
    startedAtRef.current = new Date();
    let timeoutId = null;

    const run = async () => {
      for (let r = 0; r < repeats; r++) {
        if (cancelledRef.current) return;
        setRepeatIdx(r);
        const order = buildRound();
        for (let i = 0; i < order.length; i++) {
          if (cancelledRef.current) return;
          const step = order[i];
          setStepIdx(i);
          setCurrentColor(step.color);
          tappedThisStepRef.current = false;
          if (target && step.color.toLowerCase() === target) {
            targetShownAtRef.current = performance.now();
          } else {
            targetShownAtRef.current = null;
          }
          await new Promise((res) => { timeoutId = setTimeout(res, step.duration_ms); });
        }
        if (restMs > 0 && r < repeats - 1) {
          setCurrentColor("#0A0A0A");
          await new Promise((res) => { timeoutId = setTimeout(res, restMs); });
        }
      }
      if (cancelledRef.current) return;
      setCurrentColor("#0A0A0A");
      setPhase("done");
    };

    run();
    return () => {
      cancelledRef.current = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [phase, sequence, repeats, restMs, target, randomize, targetProb]);

  // Guardar resultado al terminar
  useEffect(() => {
    if (phase !== "done" || !startedAtRef.current) return;
    const avg = reactionTimes.length
      ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
      : null;
    api.post("/neuroscience/results", {
      config_id: config.config_id,
      started_at: startedAtRef.current.toISOString(),
      finished_at: new Date().toISOString(),
      total_taps: taps,
      correct_taps: correctTaps,
      avg_reaction_ms: avg,
    }).catch(() => {});
  }, [phase, config.config_id, reactionTimes, taps, correctTaps]);

  const handleTap = () => {
    if (phase !== "playing") return;
    if (!target) return;
    if (tappedThisStepRef.current) return;
    tappedThisStepRef.current = true;
    setTaps((t) => t + 1);
    if (targetShownAtRef.current) {
      const dt = performance.now() - targetShownAtRef.current;
      setReactionTimes((arr) => [...arr, dt]);
      setCorrectTaps((c) => c + 1);
    } else {
      // tap sin estar el color target visible → falso positivo
      // no descontamos, pero queda contado como tap total
    }
  };

  const replay = () => {
    setPhase("playing");
    setStepIdx(-1);
    setRepeatIdx(0);
    setTaps(0);
    setCorrectTaps(0);
    setReactionTimes([]);
    setCurrentColor("#0A0A0A");
  };

  const handleClose = () => {
    cancelledRef.current = true;
    onClose?.();
  };

  // Render
  const avgMs = reactionTimes.length
    ? Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length)
    : null;

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col"
      style={{ backgroundColor: phase === "playing" ? currentColor : "#0A0A0A", transition: "background-color 60ms linear" }}
      onClick={handleTap}
      data-testid="color-lights-player"
    >
      {/* Cerrar */}
      <button
        onClick={(e) => { e.stopPropagation(); handleClose(); }}
        className="absolute top-3 right-3 z-10 w-10 h-10 flex items-center justify-center bg-black/60 text-white"
        data-testid="player-close"
      >
        <X size={18} weight="bold" />
      </button>

      {phase === "intro" && (
        <div className="m-auto max-w-md text-center text-white p-6">
          <div className="font-display font-black uppercase text-3xl tracking-tighter mb-3">{config.name}</div>
          {config.description && <p className="text-muted mb-6 text-sm leading-relaxed">{config.description}</p>}

          <div className="bg-[#141414] border border-app p-4 mb-6 text-left text-sm space-y-2">
            <div className="flex justify-between"><span className="text-muted">Pasos:</span> <span>{sequence.length}</span></div>
            <div className="flex justify-between"><span className="text-muted">Repeticiones:</span> <span>{repeats}</span></div>
            <div className="flex justify-between"><span className="text-muted">Orden:</span> <span className={randomize ? "text-primary-app font-bold" : ""}>{randomize ? "Aleatorio" : "Fijo"}</span></div>
            <div className="flex justify-between"><span className="text-muted">Duración total aprox:</span>
              <span>{Math.round(((sequence.reduce((a, s) => a + (s.duration_ms || 0), 0) * repeats) + (restMs * Math.max(0, repeats - 1))) / 1000)}s</span>
            </div>
            {target && (
              <div className="mt-3 pt-3 border-t border-app">
                <div className="text-xs uppercase tracking-widest text-muted mb-2">Objetivo</div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 inline-block border border-white" style={{ backgroundColor: target }} />
                  <span className="text-sm">{config.target_label || `Tocá la pantalla cuando veas el color`}</span>
                </div>
                {randomize && (
                  <div className="text-[11px] text-muted">
                    Aparece en ~<span className="text-primary-app font-bold">{Math.round(targetProb * 100)}%</span> de los pasos.
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); setPhase("playing"); }}
            className="btn-primary w-full !py-4 text-base flex items-center justify-center gap-2"
            data-testid="player-start"
          >
            <Play size={20} weight="fill" /> Empezar
          </button>
        </div>
      )}

      {phase === "playing" && (
        <div className="absolute top-3 left-3 text-white/70 text-[10px] uppercase tracking-widest font-bold pointer-events-none select-none">
          {repeatIdx + 1}/{repeats} · {stepIdx + 1}/{sequence.length}
        </div>
      )}

      {phase === "done" && (
        <div className="m-auto max-w-md text-center text-white p-6">
          <CheckCircle size={48} weight="duotone" className="text-[#32D74B] mx-auto mb-4" />
          <div className="font-display font-black uppercase text-3xl tracking-tighter mb-4">¡Listo!</div>
          {target ? (
            <div className="bg-[#141414] border border-app p-4 mb-6 grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted">Taps</div>
                <div className="font-display font-black text-2xl mt-1">{taps}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted">Aciertos</div>
                <div className="font-display font-black text-2xl mt-1 text-[#32D74B]">{correctTaps}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted">RT prom</div>
                <div className="font-display font-black text-2xl mt-1 text-primary-app">{avgMs ? `${avgMs}ms` : "—"}</div>
              </div>
            </div>
          ) : (
            <p className="text-muted mb-6">Secuencia completada. Buen trabajo.</p>
          )}
          <div className="flex gap-3">
            <button onClick={(e) => { e.stopPropagation(); replay(); }} className="btn-secondary flex-1 flex items-center justify-center gap-2" data-testid="player-replay">
              <ArrowCounterClockwise size={16} weight="bold" /> Repetir
            </button>
            <button onClick={(e) => { e.stopPropagation(); handleClose(); }} className="btn-primary flex-1" data-testid="player-finish">
              Terminar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
